import { motion, useReducedMotion } from "framer-motion";
import {
  ShieldAlert,
  Activity,
  Scale,
  FileCheck2,
  ChevronRight,
} from "lucide-react";
import { Btn } from "@/components/site/Primitives";
import { SCENARIO } from "@/lib/scenario";
import { scrollToId } from "@/lib/site";
import { track } from "@/lib/analytics";

const HEADLINE_LINES = [
  "The Commercial Intelligence",
  "Platform for Global",
  "Maritime Trade",
];

function MaskedHeadline() {
  const reduce = useReducedMotion();
  const cls =
    "display text-[2.6rem] leading-[1.05] text-natsu-warm-white min-[400px]:text-[2.8rem] sm:text-[3rem] lg:text-[3.6rem]";
  if (reduce) {
    return <h1 className={cls}>{HEADLINE_LINES.join(" ")}</h1>;
  }
  return (
    <h1 className={cls}>
      {HEADLINE_LINES.map((line, i) => (
        <span key={i} className="reveal-mask">
          <motion.span
            className="block"
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </h1>
  );
}

function fade(delay) {
  return {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
  };
}

function Metric({ label, value, accent = false }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5">
      <p className="text-[11px] leading-tight text-natsu-warm-white/50">{label}</p>
      <p
        className={`mt-1.5 font-display text-[15px] leading-tight sm:text-base ${
          accent ? "text-natsu-gold" : "text-natsu-warm-white"
        }`}
      >
        {value}
      </p>
    </div>
  );
}

const SUPPORT_ROWS = [
  {
    icon: Activity,
    tint: "text-natsu-blue-soft",
    title: "Commercial Event",
    detail: "Disruption detected · 3 contracts · 2 vessels",
  },
  {
    icon: Scale,
    tint: "text-natsu-gold",
    title: "Commercial & Financial Reasoning",
    detail: "Contract reviewed · Exposure modelled · 4 scenarios",
  },
  {
    icon: FileCheck2,
    tint: "text-natsu-purple",
    title: "Evidence & Traceability",
    detail: "Contract linked · Operational evidence linked · Source quality high",
  },
];

export default function Hero() {
  const reduce = useReducedMotion();
  const M = reduce ? "div" : motion.div;

  return (
    <section id="hero" className="on-dark grain relative overflow-hidden bg-natsu-navy" data-testid="hero-section">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-40 top-10 h-[520px] w-[520px] rounded-full bg-natsu-blue/10 blur-[140px]" />
        <div className="absolute right-0 top-1/3 h-[420px] w-[420px] rounded-full bg-natsu-gold/[0.06] blur-[150px]" />
      </div>

      <div className="site-container relative z-[1] grid grid-cols-1 items-center gap-10 pb-20 pt-28 lg:grid-cols-[0.92fr_1.08fr] lg:gap-16 lg:pb-32 lg:pt-36">
        {/* LEFT — copy */}
        <div>
          <M {...(reduce ? {} : fade(0.05))}>
            <div className="flex items-center gap-3">
              <span className="h-px w-8 bg-natsu-gold" />
              <span className="eyebrow text-natsu-warm-white/70">
                Commercial Intelligence Infrastructure
              </span>
            </div>
          </M>

          <div className="mt-6">
            <MaskedHeadline />
          </div>

          <M {...(reduce ? {} : fade(0.55))}>
            <p className="mt-6 measure text-[15px] leading-relaxed text-natsu-warm-white/70 sm:text-lg">
              NATSU Intelligence connects contractual, financial, operational,
              regulatory and market information through a proprietary commercial
              knowledge and reasoning architecture—transforming fragmented
              information into explainable, decision-ready intelligence.
            </p>
          </M>

          <M {...(reduce ? {} : fade(0.68))}>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Btn variant="gold" to="pilot" withArrow testId="hero-request-pilot" onClick={() => track("request_pilot_clicked")} className="w-full sm:w-auto">
                Request a Pilot
              </Btn>
              <button
                onClick={() => { track("explore_platform_clicked"); scrollToId("product"); }}
                data-testid="hero-explore-platform"
                className="group inline-flex items-center justify-center gap-2 text-sm font-semibold tracking-tight text-natsu-warm-white/80 transition-colors hover:text-natsu-warm-white"
              >
                Explore the Platform
                <ChevronRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </M>
        </div>

        {/* RIGHT — Executive Decision Intelligence teaser */}
        <M {...(reduce ? {} : fade(0.4))} className="relative">
          <div
            className="relative z-[1] rounded-2xl border border-white/12 bg-gradient-to-b from-white/[0.07] to-white/[0.02] p-5 shadow-2xl shadow-black/40 backdrop-blur-md sm:p-6"
            data-testid="hero-decision-card"
          >
            <div className="flex items-start justify-between gap-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-natsu-warm-white/55">
                Executive Decision Intelligence™
              </p>
              <span className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-natsu-purple/15 px-2.5 py-1 text-[11px] font-medium text-natsu-purple ring-1 ring-natsu-purple/25">
                <ShieldAlert className="h-3 w-3" /> Human Review Required
              </span>
            </div>

            <p className="mt-4 text-[13px] leading-relaxed text-natsu-warm-white/60">
              {SCENARIO.situation}
            </p>

            {/* Recommended action */}
            <div className="mt-4 rounded-xl border border-natsu-gold/25 bg-natsu-gold/[0.06] p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-natsu-gold/80">
                Recommended action
              </p>
              <p className="mt-1.5 font-display text-xl leading-snug text-natsu-warm-white sm:text-2xl">
                {SCENARIO.action}
              </p>
            </div>

            {/* 4 decision metrics */}
            <div className="mt-4 grid grid-cols-2 gap-3">
              <Metric label="Estimated Exposure Range" value={SCENARIO.exposure} accent />
              <Metric label="Recommended Action Window" value={SCENARIO.window} />
              <Metric label="Recommendation Confidence" value={SCENARIO.confidence} />
              <Metric label="Scenarios Evaluated" value={SCENARIO.scenariosEvaluated} />
            </div>

            {/* Primary drivers */}
            <div className="mt-4">
              <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-natsu-warm-white/45">
                Primary drivers
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {SCENARIO.drivers.map((d) => (
                  <span
                    key={d}
                    className="inline-flex items-center rounded-md bg-natsu-blue/10 px-2 py-1 text-[11.5px] text-natsu-blue-soft ring-1 ring-natsu-blue/15"
                  >
                    {d}
                  </span>
                ))}
              </div>
            </div>

            <button className="mt-5 flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 text-left transition-colors hover:bg-white/[0.05]">
              <span className="text-[12.5px] font-medium text-natsu-warm-white/80">
                Review Rationale &amp; Source Evidence
              </span>
              <ChevronRight className="h-4 w-4 text-natsu-warm-white/50" />
            </button>
          </div>

          {/* Compact supporting summary rows — visually connected */}
          <div className="relative z-[1] mt-3 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]">
            {SUPPORT_ROWS.map((r, i) => {
              const Icon = r.icon;
              return (
                <div
                  key={r.title}
                  className={`flex items-center gap-3.5 px-4 py-3 sm:px-5 ${
                    i > 0 ? "border-t border-white/[0.07]" : ""
                  }`}
                >
                  <Icon className={`h-4 w-4 flex-shrink-0 ${r.tint}`} strokeWidth={1.7} />
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-semibold text-natsu-warm-white/85">{r.title}</p>
                    <p className="truncate text-[11.5px] text-natsu-warm-white/50">{r.detail}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </M>
      </div>
    </section>
  );
}
